import React, { FC, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { UserRole } from 'network-utils';
import { FullPageLoader, NotFound } from 'learning-panel-components';
import { getHomepage } from '@services/getHomepage';
import PreloadSvgs from '@components/common/preloadSvg/preloadSvg';
import { selectRole } from '@redux/slices/userSlice';
import LearningPanel from '@components/learningPanel';
import LabContainer from '@components/labs/labContainer';
import LoginContainer from '@components/login/loginContainer';
import { QueryClientProvider } from 'react-query';
import UseQueryClient from 'useQueryClient';
import PrivateRoute from './privateRoutes';
import TenantRedirect from './tenantRedirect';

// Lazy-loaded components
const CreateAccount = React.lazy(() => import('@components/login/createAccount/createAccountContainer'));
const ForgetPasswordContainer = React.lazy(() => import('@components/login/forgetPassword/forgetPasswordContainer'));
const ResetPasswordContainer = React.lazy(() => import('@components/login/resetPassword/resetPasswordContainer'));
const SelfRegistrationContainer = React.lazy(
    () => import('@components/login/selfRegistrationContainer/selfRegistrationContainer')
);
const UserDetailsContainer = React.lazy(() => import('@components/login/userDetails/UserDetailsContainer'));
const AfterLoginWithGithub = React.lazy(() => import('@components/login/afterLoginWithGithub'));
const PrivacyPolicy = React.lazy(() => import('@components/privacyPolicy/privacyPolicy'));
const Admin = React.lazy(() => import('@components/adminPanel/admin'));

const InnerRoutes: FC = () => {
    const role: keyof typeof UserRole = useSelector(selectRole);
    const queryClient = UseQueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <PreloadSvgs />
            <BrowserRouter>
                <Suspense fallback={<FullPageLoader />}>
                    <Routes>
                        <Route path="/app/login" element={<LoginContainer />} />
                        <Route path="/app/setup-account/:token" element={<CreateAccount />} />
                        <Route path="/app/forget-password" element={<ForgetPasswordContainer />} />
                        <Route path="/app/user-details/:token" element={<UserDetailsContainer />} />
                        <Route path="/app/sign-up" element={<SelfRegistrationContainer />} />
                        <Route path="/app/after-login-with-github" element={<AfterLoginWithGithub />} />
                        <Route path="/app/create-account" element={<SelfRegistrationContainer isPLG />} />
                        <Route path="/app/reset-password/:token" element={<ResetPasswordContainer />} />
                        <Route path="/app/privacy-policy" element={<PrivacyPolicy />} />
                        <Route
                            path="/app/not-found"
                            element={
                                <NotFound
                                    onGoHome={() => {
                                        if (window.top) {
                                            window.top.location.href = getHomepage(role);
                                        }
                                    }}
                                />
                            }
                        />
                        <Route
                            path="/app/admin/:tab"
                            element={
                                <PrivateRoute
                                    path="/app/admin/:tab"
                                    component={Admin}
                                    permissionLevel={['manager', 'admin', 'superAdmin']}
                                />
                            }
                        />
                        <Route
                            path="/app/lab/:labUuid"
                            element={
                                <PrivateRoute
                                    path="/app/lab/:labUuid"
                                    component={LabContainer}
                                    permissionLevel={['user', 'manager', 'admin', 'superAdmin']}
                                />
                            }
                        />
                        <Route
                            path="/app/admin/:tab/:subTab"
                            element={
                                <PrivateRoute
                                    path="/app/admin/:tab/:subTab"
                                    component={Admin}
                                    permissionLevel={['manager', 'admin', 'superAdmin']}
                                />
                            }
                        />
                        <Route
                            path="/app/:tab/:subTab"
                            element={
                                <TenantRedirect>
                                    <PrivateRoute
                                        path="/app/:tab/:subTab"
                                        component={LearningPanel}
                                        permissionLevel={['user', 'manager', 'admin', 'superAdmin']}
                                    />
                                </TenantRedirect>
                            }
                        />
                        <Route
                            path="/app/:tab"
                            element={
                                <TenantRedirect>
                                    <PrivateRoute
                                        path="/app/:tab"
                                        component={LearningPanel}
                                        permissionLevel={['user', 'manager', 'admin', 'superAdmin']}
                                    />
                                </TenantRedirect>
                            }
                        />
                        <Route
                            path="*"
                            element={
                                <NotFound
                                    onGoHome={() => {
                                        if (window.top) {
                                            window.top.location.href = getHomepage(role);
                                        }
                                    }}
                                />
                            }
                        />
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </QueryClientProvider>
    );
};

export default InnerRoutes;
